import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Coffee, Loader2, Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { setAuth, isAuthenticated } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Get backend URL for image
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const loginImageUrl = `${backendUrl}/api/assets/login_image`;

  // Redirect if already authenticated
  if (isAuthenticated) {
    navigate('/');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }

    setLoading(true);
    try {
      const response = await authApi.login(email, password);
      const { access_token, user } = response.data;
      setAuth(user, access_token);
      toast.success(`Welcome back, ${user.name}!`);
      navigate('/');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 bg-coffee-espresso relative items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-coffee-espresso via-coffee-espresso/95 to-black/80" />
        <div className="relative z-10 flex flex-col items-center justify-center p-12 text-white">
          <img 
            src={loginImageUrl} 
            alt="Brothers Coffee Roastery"
            className="max-w-md w-full h-auto mb-8 drop-shadow-2xl"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <p className="text-lg text-white/80 max-w-md text-center">
            Your comprehensive sales dashboard powered by Loyverse. Track sales, 
            analyze performance, and make data-driven decisions.
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          {/* Logo for mobile */}
          <div className="lg:hidden flex flex-col items-center justify-center gap-3 mb-8">
            <img 
              src={loginImageUrl} 
              alt="Brothers Coffee Roastery"
              className="max-w-[200px] w-full h-auto"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <p className="text-sm text-muted-foreground">Sales Dashboard</p>
          </div>

          <Card className="border-0 shadow-card-hover">
            <CardHeader className="text-center pb-2">
              <div className="hidden lg:flex justify-center mb-4">
                <div className="w-14 h-14 rounded-xl bg-coffee-espresso flex items-center justify-center">
                  <Coffee className="w-7 h-7 text-coffee-cream" />
                </div>
              </div>
              <CardTitle className="font-serif text-2xl">Sign In</CardTitle>
              <CardDescription>
                Enter your credentials to access the dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11"
                    data-testid="login-email"
                    disabled={loading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-11 pr-10"
                      data-testid="login-password"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-coffee-espresso hover:bg-coffee-espresso/90 rounded-xl"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>

              <div className="mt-4 flex justify-between text-sm">
                <a
                  href="/forgot-password"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </a>
                <a
                  href="/forgot-email"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="forgot-email-link"
                >
                  Forgot your email?
                </a>
              </div>

              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Powered by Loyverse POS Integration
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;
